import { useState, useEffect } from 'react'
import client from './api'
import { useAuthContext } from './AuthProvider'

/**
 * Re-export des AuthProvider-Contexts unter dem gewohnten Namen, damit
 * bestehende Components (Layout, App, Login) ohne Änderung weiterlaufen.
 * Auth-State + /auth/me-Mount-Validierung liegt im Provider.
 */
export const useAuth = useAuthContext

/** Paginierungs-Meta aus `ApiResponse.meta` — null wenn die Route keins liefert. */
export interface FetchMeta {
  total: number
  limit: number
  offset: number
}

export function useFetch<T>(url: string | null) {
  const [data, setData] = useState<T | null>(null)
  const [meta, setMeta] = useState<FetchMeta | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    if (!url) {
      setData(null)
      setMeta(null)
      setLoading(false)
      return
    }
    let cancelled = false
    const fetch = async () => {
      setLoading(true)
      setError(null)
      try {
        const response = await client.get<any>(url)
        if (cancelled) return
        if (response.data.success) {
          setData(response.data.data)
          setMeta(response.data.meta ?? null)
        } else {
          setError(new Error(response.data.error ?? 'Request failed'))
        }
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err : new Error('Unknown error'))
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    void fetch()
    return () => {
      cancelled = true
    }
  }, [url])

  return { data, meta, loading, error }
}

export function useSimulation() {
  const [simulationData, setSimulationData] = useState<any>(null)
  const [loading, setLoading] = useState(false)

  const create = async (name: string, description: string, baseDate: string, parameters: any) => {
    setLoading(true)
    try {
      const response = await client.post('/scenarios', {
        name,
        description,
        base_date: baseDate,
        parameters,
      })
      if (response.data.success) {
        setSimulationData(response.data.data)
      }
    } catch (error) {
      console.error('Simulation creation failed:', error)
      throw error
    } finally {
      setLoading(false)
    }
  }

  return { simulationData, loading, create }
}
